import React, { useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import axios from 'axios';
import Header from './Header';
import Footer from './Footer';

export default function ResetPassword() {
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [isChanged, setIsChanged] = useState(false);
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  // Grabs the unique token from the URL route parameters
  const { token } = useParams();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    // 1. Basic empty field validation
    if (!password || !confirmPassword) {
      setError('Please fill in all fields.');
      return;
    }

    // 2. Client-side password matching check
    if (password !== confirmPassword) {
      setError('Passwords do not match. Please try again.');
      return;
    }

    // 3. Length validation (aligned with your 6-10 character backend registration rules)
    if (password.length < 6 || password.length > 10) {
      setError('Password must be between 6 and 10 characters long.');
      return;
    }

    setIsLoading(true);

    try {
      // Sending request to backend
      const response = await axios.post(`https://land-survey-main-server.onrender.com/resetPassword/${token}`, { password });
      
      // Verifies backend explicitly confirms 'status: true' before transitioning views
      if (response?.data?.status === true) {
        setIsChanged(true);
      } else {
        setError(response?.data?.message || 'Failed to update password. Try requesting a new link.');
      }
    } catch (err) {
      // Catches JWT expiration, invalid tokens, or server crashes
      const errMsg = err.response?.data?.message || 'Token expired or invalid. Please request a new link.';
      setError(errMsg);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <>
      <Header />
      <div className="flex items-center justify-center min-h-screen px-4 bg-gray-50 sm:px-6 lg:px-8">
        <div className="w-full max-w-md p-8 space-y-8 bg-white border border-gray-100 shadow-xl rounded-2xl">
          
          {!isChanged ? (
            <>
              {/* Form Header */}
              <div className="text-center">
                <div className="flex items-center justify-center w-12 h-12 mx-auto text-indigo-600 bg-indigo-100 rounded-full">
                  <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-6 h-6">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M16.5 10.5V6.75a4.5 4.5 0 1 0-9 0v3.75m-.75 11.25h10.5a2.25 2.25 0 0 0 2.25-2.25v-6.75a2.25 2.25 0 0 0-2.25-2.25H6.75a2.25 2.25 0 0 0-2.25 2.25v6.75a2.25 2.25 0 0 0 2.25 2.25Z" />
                  </svg>
                </div>
                <h2 className="mt-4 text-3xl font-extrabold text-gray-900">
                  Set new password
                </h2>
                <p className="mt-2 text-sm text-gray-600">
                  Your new password must be different from previously used passwords.
                </p>
              </div>

              {/* Input Form */}
              <form className="mt-8 space-y-6" onSubmit={handleSubmit}>
                {error && (
                  <div className="p-3 text-sm text-red-600 border border-red-200 rounded-lg bg-red-50">
                    {error}
                  </div>
                )}
                
                <div className="space-y-4">
                  <div>
                    <label htmlFor="password" className="block text-sm font-medium text-gray-700">
                      New Password
                    </label>
                    <input
                      id="password"
                      name="password"
                      type="password"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      className="block w-full px-3 py-2 mt-1 placeholder-gray-400 transition duration-150 border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                      placeholder="••••••••"
                    />
                    <p className="mt-1 text-xs text-gray-500">Must be between 6 and 10 characters.</p>
                  </div>

                  <div>
                    <label htmlFor="confirm-password" className="block text-sm font-medium text-gray-700">
                      Confirm New Password
                    </label>
                    <input
                      id="confirm-password"
                      name="confirmPassword"
                      type="password"
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                      className="block w-full px-3 py-2 mt-1 placeholder-gray-400 transition duration-150 border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                      placeholder="••••••••"
                    />
                  </div>
                </div>

                <div>
                  <button
                    type="submit"
                    disabled={isLoading}
                    className="group relative w-full flex justify-center py-2.5 px-4 border border-transparent text-sm font-medium rounded-lg text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 transition duration-150 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {isLoading ? (
                      <span className="flex items-center gap-2">
                        <svg className="w-5 h-5 text-white animate-spin" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                        Updating password...
                      </span>
                    ) : (
                      'Reset password'
                    )}
                  </button>
                </div>
              </form>
            </>
          ) : (
            /* Controlled Success State */
            <div className="py-4 text-center">
              <div className="flex items-center justify-center w-12 h-12 mx-auto text-green-600 bg-green-100 rounded-full">
                <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-6 h-6">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M9 12.75 11.25 15 15 9.75M21 12a9 9 0 1 1-18 0 9 9 0 0 1 18 0Z" />
                </svg>
              </div>
              <h2 className="mt-4 text-3xl font-extrabold text-gray-900">
                Password reset
              </h2>
              <p className="max-w-sm mx-auto mt-2 text-sm text-gray-600">
                Your password has been successfully reset. You can now safely log in with your new credentials.
              </p>
              <div className="mt-6">
                <Link
                  to="/login"
                  className="w-full flex justify-center py-2.5 px-4 border border-transparent text-sm font-medium rounded-lg text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none transition duration-150 text-center"
                >
                  Continue to login
                </Link>
              </div>
            </div>
          )}
        </div>
      </div>
      <Footer />
    </>
  );
}